import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Users, CheckCircle, XCircle, Loader } from "lucide-react";
import { API_BASE_URL } from "../../config/apiConfig";
import bg from "../../assets/first_bg.png";

/**
 * JoinParentPage
 *
 * Handles the shareable parent invite link:
 *   /join-parent?code=PC-{parentUserId}
 *
 * Behaviour:
 *  - Logged in as student  → calls POST /api/parents/join/:code → success screen
 *  - Not logged in         → saves code to localStorage, redirects to /login
 *  - Logged in as non-student → shows an error
 */
const JoinParentPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [status, setStatus] = useState("loading");
    const [message, setMessage] = useState("");
    const [parentName, setParentName] = useState("");

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const code = params.get("code") || "";

        if (!code) {
            setStatus("error");
            setMessage("No parent code found in the link. Please ask your parent to share the correct link.");
            return;
        }

        const token = localStorage.getItem("token");

        if (!token) {
            localStorage.setItem("pending_parent_code", code);
            setStatus("needs_login");
            return;
        }

        const joinParent = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/parents/join/${code}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                const data = await res.json();

                if (res.ok) {
                    setParentName(data.parentName || "your parent");
                    setStatus("success");
                    localStorage.removeItem("pending_parent_code");
                    setTimeout(() => navigate("/dashboard"), 3000);
                } else {
                    setStatus("error");
                    setMessage(data.message || "Failed to link to parent account. Please try again.");
                }
            } catch {
                setStatus("error");
                setMessage("A network error occurred. Check your connection and try again.");
            }
        };

        joinParent();
    }, [location.search, navigate]);

    return (
        <div
            className="flex justify-center items-center min-h-screen py-4"
            style={{
                backgroundImage: `url(${bg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundColor: "#2148C0",
            }}
        >
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 md:p-12 max-w-md w-[90%] text-center">
                <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                        <Users className="w-9 h-9 text-white" />
                    </div>
                </div>

                <h1 className="text-2xl font-bold text-white mb-2">TeeChaa</h1>
                <p className="text-white/70 text-sm mb-8">Parent Invite</p>

                {/* Loading */}
                {status === "loading" && (
                    <div>
                        <Loader className="w-10 h-10 text-white mx-auto mb-4 animate-spin" />
                        <p className="text-white font-medium">Linking you to your parent's account…</p>
                    </div>
                )}

                {/* Success */}
                {status === "success" && (
                    <div>
                        <CheckCircle className="w-14 h-14 text-green-300 mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-white mb-2">You're linked!</h2>
                        <p className="text-white/80 mb-6">
                            You've been successfully connected to <strong className="text-white">{parentName}</strong>'s family account.
                        </p>
                        <p className="text-white/60 text-sm">Taking you to your dashboard…</p>
                    </div>
                )}

                {/* Needs login */}
                {status === "needs_login" && (
                    <div>
                        <div className="w-14 h-14 bg-yellow-300/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Users className="w-8 h-8 text-yellow-200" />
                        </div>
                        <h2 className="text-xl font-bold text-white mb-3">Log in to link up</h2>
                        <p className="text-white/70 text-sm mb-6 leading-relaxed">
                            You need a TeeChaa account to connect to your parent. Log in and we'll finish the link automatically.
                        </p>
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() => navigate("/login")}
                                className="w-full py-3 bg-white text-[#2148C0] font-bold rounded-xl hover:bg-gray-100 transition-colors"
                            >
                                Log In
                            </button>
                            <button
                                onClick={() => {
                                    const params = new URLSearchParams(location.search);
                                    const code = params.get("code") || "";
                                    navigate(`/signup?parent_code=${encodeURIComponent(code)}`);
                                }}
                                className="w-full py-3 bg-white/20 hover:bg-white/30 text-white font-medium rounded-xl transition-colors"
                            >
                                Create Account
                            </button>
                        </div>
                    </div>
                )}

                {/* Error */}
                {status === "error" && (
                    <div>
                        <XCircle className="w-14 h-14 text-red-300 mx-auto mb-4" />
                        <h2 className="text-xl font-bold text-white mb-3">Something went wrong</h2>
                        <p className="text-white/70 text-sm mb-6 leading-relaxed">{message}</p>
                        <button
                            onClick={() => navigate("/dashboard")}
                            className="w-full py-3 bg-white text-[#2148C0] font-bold rounded-xl hover:bg-gray-100 transition-colors"
                        >
                            Go to Dashboard
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default JoinParentPage;
